export enum Plan {
  PCP = 'Pension Choices Plan',
  FRP = 'Flexible Retirement Plan',
  RA = 'Retirement Account',
  OTHER = 'Other',
}

export enum Gender {
  Male = 'Male',
  Female = 'Female',
}

export enum Country {
  England = 'England',
  Scotland = 'Scotland',
  NorthernIreland = 'Northern Ireland',
  Wales = 'Wales',
}

export enum Frequency {
  Monthly = 'Monthly',
  Yearly = 'Yearly',
}

export enum IsCash {
  Yes = 'Yes',
  No = 'No',
}

export const sectionWrapperContent = {
  aboutYouTitle: 'About you',
  aboutYouTooltip: '<p>We won’t use any of this data to identify you, it will only be used to calculate how long you might need your pension pot to last.</p>',
  yourPlanTitle: 'Your plan',
  yourPlanTooltip: '<p>If you have more than one Prudential plan already in drawdown you will need to complete the calculator for each plan.</p>',
  changesToIncomeTitle: 'Changes to income',
  changesToIncomeTooltip: '<p>You can select a monthly or annual amount. For quarterly amounts please divide accordingly.</p><br/><p>If you are planning on taking a single lump sum you can input the amount or a percentage and we will calculate that for you.</p>',
  [`infoText${IsCash.No}`]: '<p>The calculator assumes your pension pot is invested in a fund with an expected growth rate of 5% and an annual management charge of 1.35%. These rates may vary for your individual funds.</p><br/><p>If a large portion of your pension pot is invested in cash, the projected rate of return used by the calculator may present a more favourable growth rate than you are currently likely to receive.</p>',
  [`infoText${IsCash.Yes}`]: '<p>The calculator assumes your pension pot is invested in a fund with an expected growth rate of 0% and an annual management charge of 1%. These rates may vary for your individual funds.</p><p>The tool does not take into account changes to inflation, which means that the buying power of your money could be less in the future.</p>',
  genderLabel: 'Are you...',
  birthDateLabel: 'Date of birth:',
  residenceLabel: 'Where do you live?',
  residenceDropdownLabel: 'Select country',
  planLabel: 'Prudential plan:',
  planDropdownLabel: 'Choose your plan',
  planValueLabel: 'Current value of your plan:',
  cashLabel: 'Are you mainly invested in a cash fund?',
  currentIncomeLabel: 'Current income from drawdown:',
  plannedIncomeLabel: 'Planned income from drawdown:',
  lumpsumLabel: 'Single lump-sum withdrawal amount:',
  lumpsumPlaceholder: 'Enter amount (optional)',
  calculateButton: 'Calculate',
  updateButton: 'Update results',
};

export const inputContent = {
  textInputPlaceholder: 'Enter amount',
  dropdownInputPlaceholder: 'Open...',
};

export const importantInfoContent = {
  lifeExpectancy: '<p>Life-expectancy figures have been calculated using data from the Office for National Statistics and do not take into account your individual health circumstances.</p>',
  considerations: '<ul><li>Everyone’s individual circumstances are different and this information is only a guide to give you an idea of how long your money might last. You might need to pay tax depending on your circumstances and the options you choose. Tax rules can also change in the future.</li><br/><li>The calculator doesn’t take into account additional contributions and changes you may wish to make to your plan in the future. Also bear in mind that your fund value doesn’t take into account inflation so your money might not buy as much in the future.</li><br/><li>Money in the drawdown plan remains invested. With any investments the value can go down as well as up and you may get back less than you put in.</li></ul>',
  printResults: 'Print these results',
};

export const nextStepsContent = {
  title: 'What next?',
  buttonText: 'Download form',
  [Plan.PCP]: '<p>To change your current income amount from your drawdown plan or to take a lump sum, download, complete and post the change of income form to us.</p>',
  [Plan.FRP]: '<p>To change your current income amount from your drawdown plan or to take a lump sum, download, complete and post the change of income form to us.</p>',
};

export const callUsContent = {
  title: 'Talk to us',
  [Plan.PCP]: '<p>If you’d like to speak to someone about your options, we’re here to help. We can’t give you advice or make your decision for you but we’re happy to talk through your options and their possible implications.</p> <br /><p>Call us on <a href="tel:08082342372" data-test="soi-contact-us-link-phone">0808 234 2372</a>. We’re here 9am–5pm, Monday to Friday.</p><p>We might record your call to make sure our service is up to standard.</p>',
  [Plan.FRP]: '<p>If you’d like to speak to someone about your options, we’re here to help. We can’t give you advice or make your decision for you but we’re happy to talk through your options and their possible implications.</p> <br /><p>Call us on <a href="tel:08082342372" data-test="soi-contact-us-link-phone">0808 234 2372</a>. We’re here 9am–5pm, Monday to Friday.</p><p>We might record your call to make sure our service is up to standard.</p>',
  [Plan.RA]: '<p>If you’d like to speak to someone about your options, we’re here to help. We can’t give you advice or make your decision for you but we’re happy to talk through your options and their possible implications.</p> <br /><p>Call us on <a href="tel:03452680488" data-test="soi-contact-us-link-phone">0345 268 0488</a> or <a href="tel:+442037559358" data-test="soi-contact-us-link-phone">+44 203 755 9358</a> if calling from abroad. We’re here 8:30am–6:00pm, Monday to Friday.</p><p>We might record your call to make sure our service is up to standard.</p>',
  [Plan.OTHER]: '<p>If you’d like to speak to someone about your options, we’re here to help. We can’t give you advice or make your decision for you but we’re happy to talk through your options and their possible implications.</p> <br /><p>Call us on <a href="tel:0800000000" data-test="soi-contact-us-link-phone">0800 000 000</a>. We’re here 8am–6pm, Monday to Friday.</p><p>We might record your call to make sure our service is up to standard.</p>',
};

export const formLinks = {
  [Plan.PCP]: 'https://www.mandg.com/dam/pru/shared/documents/en/pcpf100001.pdf',
  [Plan.FRP]: 'https://www.mandg.com/dam/pru/shared/documents/en/pppk12501.pdf',
};

export const errors = {
  isRequired: 'This field is required',
  isNumber: 'Please enter a valid number',
  isLessThanOneMillion: 'Please enter a number under £1,000,000',
  isDateValid: 'Please enter a valid date',
  isOver18: 'You need to be over 18 years old',
  isAfter1900: 'Date cannot be before 1900',
  isInThePast: 'Date cannot be in the future',
  isPercentage: 'Please enter a number under 100',
  isMoreThan0: 'Value must be over £0 ',
};

// Warning: changing FMC and Growth values may break calculator and calculatorHelpers tests
export const FMCs = {
  [IsCash.No]: 1.35,
  [IsCash.Yes]: 1.00,
};

export const growths = {
  [IsCash.No]: 5.0,
  [IsCash.Yes]: 0,
};

export const calcConfig = {
  lifeExpectancyProbabilityPercentage: 50,
  IncomeEscalation: 0,
  IncomeTerm: 120,
  inputModel: {
    GrowthVariance: [],
    CapitalGtees: [],
    RegularConts: [],
    RegularContsVariance: [],
    PlannedConts: [],
    TaxRate: 20,
    IncomeVariance: [],
    PlannedIncome: [],
    IncomeGtees: [],
    PCLSPercentage: 0,
    IACPlannedType: 'P',
    IACPlannedAmount: 0,
    IACRegsType: 'P',
    IACRegsAmount: 0,
    IACRegsPeriod: 12,
    OACType: 'P',
    OACAmount: 0,
    AdhocCharges: [],
    TargetFund: 0,
    OtherIncomeInput: [],
  },
};

export const analytics = {
  buttonsId: 'buttons',
  calculate: 'changes to income calc - calculate',
  update: 'changes to income calc - update results',
  download: 'changes to income calc - download form',
};



// WEBPACK FOOTER //
// ./src/content/content.ts